<template>
  <div class="page-default">
    <div class="row m-0 page-section">
      <div
        class="col col-md-5 col-lg-4 section-left"
        :class="{ show: showInboxList }"
      >
        <div class="case-container">
          <div class="case-header text-capitalize text-dark">
            <div class="case-title text-truncate">
              <button
                class="btn btn-link"
                @click="switchCaseSource('all')"
                :class="{ active: currentCaseSource == 'all' }"
              >
                All Cases
              </button>
              <button
                class="btn btn-link"
                @click="switchCaseSource('socialMedia')"
                :class="{ active: currentCaseSource == 'socialMedia' }"
              >
                Social Media
              </button>
              <button
                class="btn btn-link"
                @click="switchCaseSource('mobileApp')"
                :class="{ active: currentCaseSource == 'mobileApp' }"
              >
                Mobile App
              </button>
              <button
                class="btn btn-link"
                @click="switchCaseSource('agent')"
                :class="{ active: currentCaseSource == 'agent' }"
              >
                Agent
              </button>
            </div>
            <div
              class="case-header-icon clickable d-block d-md-none"
              @click="toggleInboxList()"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </div>
          </div>
          <div class="case-body">
            <div
              class="case-list"
              :class="{ active: currentCase == c }"
              v-for="c in computedCases"
              :key="c._id"
              :id="`case${c._id}`"
              @click="goTo('inbox', c._id)"
            >
              <div class="media">
                <div class="d-flex align-items-center justify-content-between">
                  <font-awesome-icon
                    :icon="getCaseIcon(c.type).i"
                    class="fa-2x mr-2 media-icon"
                    :class="`text-${getCaseIcon(c.type).color}`"
                  />
                  <img
                    :src="c.socmedAvatarUrl"
                    class="mr-3 media-image"
                    v-if="c.source == 'socialMedia' && c.socmedAvatarUrl"
                    @error="onImageError(c)"
                  />
                  <img
                    src="@/assets/user.jpg"
                    class="mr-2 media-image"
                    v-else
                  />
                </div>
                <div class="media-body">
                  <p class="media-title mb-0">
                    {{
                      c.source == "socialMedia"
                        ? c.socmedUsername
                        : c.user
                        ? c.user.name
                        : "User data unavailable"
                    }}
                  </p>
                  <a
                    class="text-muted small mb-2"
                    href="#"
                    @click.stop.prevent=""
                    :title="getMoment('title', c.timestamp)"
                  >
                    {{ getMoment("display", c.timestamp) }}
                  </a>
                  <p class="mt-3">
                    <text-highlighter
                      :type="c.type"
                      :text="truncateCaseCaption(c.caption)"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div class="case-list border-0" v-if="computedCases.length < 1">
              <p class="text-muted text-center font-italic small mb-0">
                No case found
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col section-right text-truncate d-flex px-0 px-md-3">
        <div class="case-container bg-gradient flex-grow-1" v-if="currentCase">
          <div class="section-header">
            <button
              class="btn btn-outline-primary shadow-sm mr-3 section-left-toggler"
              @click="toggleInboxList()"
            >
              <font-awesome-icon :icon="['fas', 'inbox']" />
            </button>
            <div class="btn-group btn-group-pill mr-3">
              <button
                class="btn btn-custom btn-sm text-capitalize text-nowrap"
                type="button"
              >
                {{ currentCase && currentCaseStatusText }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-custom dropdown-toggle dropdown-toggle-split"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <span class="sr-only">Toggle Dropdown</span>
              </button>
              <div class="dropdown-menu dropdown-menu-right">
                <button
                  class="dropdown-item"
                  type="button"
                  @click="updateCaseStatus('solved')"
                >
                  Mark as Solved
                </button>
                <div class="dropdown-divider"></div>
                <button
                  class="dropdown-item"
                  type="button"
                  @click="updateCaseStatus('inProgress')"
                >
                  Mark as In Progress
                </button>
                <div class="dropdown-divider"></div>
                <button
                  class="dropdown-item"
                  type="button"
                  @click="generateReport()"
                  :disabled="!currentCase.project"
                >
                  Generate Report
                </button>
                <div class="dropdown-divider"></div>
                <button
                  class="dropdown-item"
                  type="button"
                  @click="openEditModal()"
                >
                  Edit
                </button>
                <div class="dropdown-divider"></div>
                <button
                  class="dropdown-item text-danger"
                  type="button"
                  @click="deleteCase(currentCase._id)"
                >
                  Delete
                </button>
              </div>
            </div>
            <div class="flex-grow-1 text-truncate">
              CASE #{{ getCaseNumber(currentCase._id) }} &middot;
              <font-awesome-icon
                :icon="getCaseIcon(currentCase.type).i"
                class="media-icon"
                :class="`text-${getCaseIcon(currentCase.type).color}`"
              />
              &middot;
              {{
                currentCase.source == "socialMedia"
                  ? currentCase.socmedUsername
                  : currentCase.user
                  ? currentCase.user.name
                  : "User data unavailable"
              }}
            </div>
          </div>
          <div
            id="commentBody"
            class="comment-body small"
            :class="{ 'has-image': selectedCommentImagesPreview.length > 0 }"
          >
            <div class="comment-list px-4 pt-4">
              <div class="media">
                <img
                  :src="currentCase.socmedAvatarUrl"
                  class="mr-3 media-image"
                  v-if="
                    currentCase.source == 'socialMedia' &&
                    currentCase.socmedAvatarUrl
                  "
                  @error="onImageError(currentCase)"
                />
                <img src="@/assets/user.jpg" class="mr-2 media-image" v-else />
                <div class="media-body">
                  <p class="media-title mb-0">
                    {{
                      currentCase.source == "socialMedia"
                        ? currentCase.socmedUsername
                        : currentCase.user
                        ? currentCase.user.name
                        : "User data unavailable"
                    }}
                  </p>
                  <a
                    class="text-muted small"
                    href="#"
                    @click.stop.prevent=""
                    :title="getMoment('title', currentCase.timestamp)"
                  >
                    {{ getMoment("display", currentCase.timestamp) }}
                  </a>
                  <p class="mt-3">
                    <text-highlighter
                      :type="currentCase.type"
                      :text="currentCase.caption"
                    />
                  </p>
                  <div class="row m-0 pt-3">
                    <div
                      class="col-6 p-1 h-100"
                      v-for="cm in currentCase.media"
                      :key="cm._id"
                      :id="`commentFile${cm._id}`"
                    >
                      <div
                        class="py-3 w-100 d-flex justify-content-center align-items-center text-muted"
                      >
                        <span
                          class="spinner-border spinner-border-sm mr-2"
                          role="status"
                        >
                        </span>
                        Loading media...
                      </div>
                      {{ loadImage(cm) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="comment-list px-4 pt-4"
              v-for="comment in comments"
              :key="comment._id"
              :id="`comment${comment._id}`"
            >
              <div class="media">
                <img src="@/assets/user.jpg" class="mr-3 media-image" />
                <div class="media-body">
                  <p class="media-title mb-0">
                    {{
                      comment.user ? comment.user.name : "User data unavailable"
                    }}
                    <span
                      v-if="['during', 'after'].includes(comment.type)"
                      class="badge badge-pill text-capitalize px-2 ml-2"
                      :class="
                        comment.type == 'after' ? 'badge-primary' : 'badge-info'
                      "
                    >
                      {{ comment.type }}
                    </span>
                  </p>
                  <a
                    class="text-muted small"
                    href="#"
                    @click.stop.prevent=""
                    :title="getMoment('title', comment.createdAt)"
                  >
                    {{ getMoment("display", comment.createdAt) }}
                  </a>
                  <p class="mt-3" v-if="comment.content">
                    <text-highlighter :text="comment.content" />
                  </p>
                  <div class="row m-0 pt-3">
                    <div
                      class="col-6 p-1 h-100"
                      v-for="cm in comment.file"
                      :key="cm._id"
                      :id="`commentFile${cm._id}`"
                    >
                      <div
                        class="py-3 w-100 d-flex justify-content-center align-items-center text-muted"
                      >
                        <span
                          class="spinner-border spinner-border-sm mr-2"
                          role="status"
                        >
                        </span>
                        Loading media...
                      </div>
                      {{ loadImage(cm) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="comment-box"
            :class="{ 'has-image': selectedCommentImagesPreview.length > 0 }"
          >
            <div class="row m-0 comment-attachment">
              <div
                class="col"
                v-for="file in selectedCommentImagesPreview"
                :key="file.name"
              >
                <img :src="file.src" />
                <div
                  class="media-delete-button"
                  @click="clearCommentImage(file.name)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'times']"
                    class="text-white clickable"
                  />
                </div>
              </div>
            </div>
            <div class="input-group">
              <div
                class="input-group-prepend comment-file-toggler p-1 clickable"
              >
                <font-awesome-icon
                  :icon="['fas', 'paperclip']"
                  class="text-dark"
                />
                <input
                  id="commentFile"
                  type="file"
                  @change="handleFileChange"
                  multiple
                />
              </div>
              <input
                id="commentInput"
                type="text"
                class="form-control form-control-default form-control-sm border-0"
                placeholder="Write a message"
                v-model="models.comment"
              />
              <div
                class="input-group-append clickable px-1"
                @click="submitComment()"
              >
                <select
                  id="selectCommentType"
                  class="custom-select custom-select-sm mr-3"
                  v-model="models.type"
                >
                  <option value="comment">Comment</option>
                  <option value="during">During</option>
                  <option value="after">After</option>
                </select>
                <font-awesome-icon
                  :icon="[
                    'fas',
                    isSubmittingComment ? 'circle-notch' : 'paper-plane',
                  ]"
                  class="text-branding"
                  :class="{ 'fa-spin': isSubmittingComment }"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          class="case-container flex-grow-1 bg-transparent d-flex justify-content-center align-items-center"
          v-else
        >
          <p class="lead text-muted mb-0">No case selected</p>
        </div>
      </div>
    </div>
    <edit-case-modal
      :show="modals.edit"
      :currentCase="currentCase"
      :iProjects="projects"
      :iRoads="roads"
      :iInspections="inspections"
      :states="states"
      @refresh="refreshModalData"
      @close="closeEditModal"
    />
    <spinner v-if="isProcessing" />
  </div>
</template>

<script>
import $ from "jquery";
import Swal from "sweetalert2";
import Moment from "moment";
import API from "@/api";
import Helper from "@/helper";
import Spinner from "@/components/Spinner";
import TextHighlighter from "@/components/TextHighlighter";
import EditCaseModal from "@/modals/EditCase.vue";

export default {
  name: "auth",
  components: {
    spinner: Spinner,
    "edit-case-modal": EditCaseModal,
    "text-highlighter": TextHighlighter,
  },
  data() {
    return {
      isProcessing: false,
      isSubmittingComment: false,
      showInboxList: false,
      currentCaseSource: "all",
      currentCase: null,
      cases: [],
      comments: [],
      states: [],
      projects: [],
      roads: [],
      inspections: [],
      selectedCommentImages: [],
      selectedCommentImagesPreview: [],
      models: {
        comment: "",
        type: "",
      },
      modals: {
        edit: false,
      },
    };
  },
  watch: {
    "$route.params.id": {
      handler() {
        let c = this.cases.find((c) => c._id == this.$route.params.id);
        if (c) {
          this.switchCurrentCase(c);
        }
      },
      immediate: false,
    },
  },
  computed: {
    computedCases() {
      if (this.currentCaseSource == "all") {
        return this.cases;
      } else {
        return this.cases.filter((c) => c.source == this.currentCaseSource);
      }
    },
    currentCaseStatusText() {
      if (this.currentCase) {
        return this.currentCase.status == "inProgress"
          ? "in progress"
          : this.currentCase.status;
      } else {
        return "";
      }
    },
  },
  methods: {
    deleteCase(caseId) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this action",
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        reverseButtons: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-sm btn-danger ml-2",
          cancelButton: "btn btn-sm btn-link mr-2",
        },
      }).then((result) => {
        if (result.value) {
          API.delete(`cases/${caseId}`).then((retVal) => {
            this.goTo("inbox");
            this.getCases();
          });
        }
      });
    },
    async refreshModalData(type) {
      if (type == "project") {
        await this.getProjects();
      } else if (type == "road") {
        await this.getRoads();
      } else if (type == "inspection") {
        await this.getInspections();
      }
    },
    getCaseNumber(id) {
      return Helper.generateCaseNumber(id);
    },
    getMoment(type, d) {
      if (!d) {
        return "-";
      } else {
        switch (type) {
          case "title":
            return Moment(d).format("DD MMMM YYYY, hh:mm A");
            break;
          case "display":
            return Moment(d).fromNow(false);
          default:
            return "";
            break;
        }
      }
    },
    onImageError(cl) {
      cl.socmedAvatarUrl = require("@/assets/user.jpg");
    },
    openEditModal() {
      this.modals.edit = true;
    },
    async closeEditModal(response, createdNewProject) {
      this.modals.edit = false;
      if (response) {
        await this.getCases(response.id);
      }
    },
    updateCaseStatus(status) {
      this.isProcessing = true;
      let putBody = { status: status };
      API.put(`cases/${this.currentCase._id}`, putBody).then((retVal) => {
        this.getCases(retVal.data._id);
      });
    },
    loadImage(cm) {
      let img = cm.mime.includes("image")
        ? new Image()
        : document.createElement("VIDEO");
      img.className = "w-100";
      let vm = this;

      if (cm.mime.includes("video")) {
        let source = document.createElement("source");
        img.onloadeddata = () => {
          let parent = document.getElementById(`commentFile${cm._id}`);
          if (parent) {
            parent.innerHTML = "";
            parent.appendChild(img);
          }
        };
        source.type = cm.mime;
        source.src = cm.url;
        img.setAttribute("controls", "controls");
        img.appendChild(source);
      } else {
        img.onload = () => {
          let parent = document.getElementById(`commentFile${cm._id}`);
          if (parent) {
            parent.innerHTML = "";
            parent.appendChild(img);
          }
        };
        img.src = cm.url;
      }
    },
    clearCommentImage(name) {
      if (!name) {
        this.selectedCommentImages = [];
        this.selectedCommentImagesPreview = [];
      } else {
        this.selectedCommentImages = this.selectedCommentImages.filter(
          (sc) => sc.name != name
        );
        this.selectedCommentImagesPreview = this.selectedCommentImagesPreview.filter(
          (sc) => sc.name != name
        );
      }
    },
    handleFileChange(event) {
      let files = event.target.files;
      files.forEach((file) => {
        let check = this.selectedCommentImages.find(
          (sc) => sc.name == file.name
        );
        if (!check) {
          this.selectedCommentImages.push(file);

          let reader = new FileReader();
          let vm = this;
          reader.onload = (e) => {
            vm.selectedCommentImagesPreview.push({
              name: file.name,
              src: e.target.result,
            });
            document.getElementById("commentFile").value = "";
          };
          reader.readAsDataURL(file);
        }
      });
    },
    truncateCaseCaption(caption) {
      let captionLength = caption.length;
      if (captionLength <= 280) {
        return caption;
      } else {
        return caption.slice(0, 280).concat("...");
      }
    },
    toggleInboxList() {
      this.showInboxList = !this.showInboxList;
    },
    switchCurrentCase(c) {
      this.toggleInboxList();
      this.currentCase = c;
      this.getComments(c._id);
      this.models.comment = "";
      this.models.type = "comment";
      this.clearCommentImage();
      const vm = this;
      this.$nextTick(() => {
        $("#commentInput").keyup((ev) => {
          if (ev.key === "Enter") {
            vm.submitComment();
          }
        });
      });
    },
    switchCaseSource(source) {
      this.currentCaseSource = source;
    },
    getCaseIcon(type) {
      return Helper.socialMediaIcons(type);
    },
    goTo(name, caseId) {
      if (!caseId) {
        this.$router.push({
          name: "inbox",
        });
      } else if (this.$route.params.id != caseId) {
        this.$router.push({
          name: "inbox",
          params: { id: caseId },
        });
      }
    },
    generateReport() {
      let state = this.states.find(
        (s) => s._id == this.currentCase.project.stateName
      );
      this.$router.push({
        name: "reports",
        query: {
          state: state.name,
          project: this.currentCase.project._id,
        },
      });
    },
    getCases(caseId) {
      this.isProcessing = true;
      API.get(
        "cases?timestamp_null=false&_sort=timestamp:DESC&_limit=-1&type_ne=waze"
      )
        .then((retVal) => {
          this.cases = retVal.data;
          if (caseId) {
            let cse = this.cases.find((c) => c._id == caseId);
            this.switchCurrentCase(cse);
            this.$nextTick(() => {
              let elem = document.getElementById(`case${cse._id}`);
              elem.scrollIntoView();
            });
          } else {
            this.switchCurrentCase(this.cases[0]);
          }
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    getComments(caseId) {
      this.comments = [];
      API.get(`comments?case=${caseId}&_limit=-1`)
        .then((retVal) => {
          this.comments = retVal.data;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    submitComment() {
      if (
        !this.isSubmittingComment &&
        (this.models.comment.length > 1 ||
          this.selectedCommentImages.length > 0)
      ) {
        this.isSubmittingComment = true;
        const postBody = {
          content: this.models.comment,
          type: this.models.type,
          case: this.currentCase._id,
          user: JSON.parse(localStorage.getItem("user"))._id,
          timestamp: Date.now(),
        };
        API.post("comments", postBody)
          .then((retVal) => {
            if (this.selectedCommentImages.length > 0) {
              this.uploadCommentImage(retVal.data);
            } else {
              this.comments.push(retVal.data);
              this.models.comment = "";
              this.models.type = "comment";
              this.clearCommentImage();
              this.isSubmittingComment = false;
              this.scrollToLatestComment(retVal.data);
            }
          })
          .catch((err) => {
            console.error(err);
            this.isSubmittingComment = false;
          });
      }
    },
    uploadCommentImage(data) {
      let fileModels = {
        ref: "comment",
        refId: data._id,
        field: "file",
        files: this.selectedCommentImages,
      };
      let formData = new FormData();
      Object.keys(fileModels).forEach((key) => {
        if (key != "files") {
          formData.append(key, fileModels[key]);
        } else {
          fileModels[key].forEach((file, index) => {
            formData.append("files", file);
          });
        }
      });

      API.postForm("upload", formData)
        .then((retVal) => {
          data.file = retVal.data;
          this.comments.push(data);
          this.models.comment = "";
          this.models.type = "comment";
          this.clearCommentImage();
          this.isSubmittingComment = false;
          this.scrollToLatestComment(data);
        })
        .catch((err) => {
          console.error(err.response);
          this.isSubmittingComment = false;
        });
    },
    getStates() {
      this.isProcessing = true;
      API.get("states?_limit=-1")
        .then((retVal) => {
          this.states = retVal.data;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    async getProjects() {
      this.isProcessing = true;
      API.get("projects?_limit=-1")
        .then((retVal) => {
          this.projects = retVal.data;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    async getRoads() {
      this.isProcessing = true;
      API.get("roads?_limit=-1")
        .then((retVal) => {
          this.roads = retVal.data;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    async getInspections() {
      this.isProcessing = true;
      API.get("inspections?_limit=-1")
        .then((retVal) => {
          this.inspections = retVal.data;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
        });
    },
    scrollToLatestComment(comment) {
      this.$nextTick(() => {
        let elem = document.getElementById(`comment${comment._id}`);
        elem.scrollIntoView();
      });
    },
  },
  async mounted() {
    await this.getProjects();
    await this.getRoads();
    await this.getInspections();
    await this.getStates();
    await this.getCases(this.$route.params.id);
  },
};
</script>
